import React, { useState, useEffect, useRef } from "react";

interface Phase {
  date: string;
  title: string;
  description: string;
  details: string;
}

interface HackTimelineProps {
  phase: Phase;
  index: number;
  isVisible: boolean;
  progress: number;
}

const HackTimeline: React.FC<HackTimelineProps> = ({
  phase,
  index,
  isVisible,
  progress,
}) => {
  return (
    <div
      className={`flex gap-6 mb-12 transition-all duration-700 ${
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Timeline dot and line */}
      <div className="flex flex-col items-center">
        <div
          className={`w-5 h-5 rounded-full border-4 transition-all duration-500 ${
            isVisible
              ? "border-red-500 bg-red-500 shadow-lg shadow-red-500/50"
              : "border-gray-700 bg-gray-800"
          }`}
          style={{ transitionDelay: `${index * 150 + 200}ms` }}
        />
        {index < 5 && (
          <div className="relative w-1 flex-1" style={{ minHeight: "120px" }}>
            {/* Background line */}
            <div className="absolute inset-0 bg-gray-800" />
            {/* Animated red line that fills based on scroll */}
            <div
              className="absolute top-0 left-0 w-full bg-gradient-to-b from-red-500 to-red-600 transition-all duration-300"
              style={{
                height: `${
                  progress >= (index + 1) / 6
                    ? 100
                    : Math.max(0, ((progress - index / 6) / (1 / 6)) * 100)
                }%`,
                boxShadow:
                  progress >= (index + 1) / 6
                    ? "0 0 10px rgba(239, 68, 68, 0.5)"
                    : "none",
              }}
            />
          </div>
        )}
      </div>

      {/* Content card */}
      <div
        className={`flex-1 pb-8 transition-all duration-500 ${
          isVisible ? "scale-100" : "scale-95"
        }`}
        style={{ transitionDelay: `${index * 150 + 100}ms` }}
      >
        <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-lg p-6 hover:border-red-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/10">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-red-500 font-bold text-sm px-3 py-1 bg-red-500/10 rounded-full border border-red-500/30">
              {phase.date}
            </span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-3 bg-gradient-to-r from-white to-red-200 bg-clip-text text-transparent">
            {phase.title}
          </h3>
          <p className="text-gray-300 leading-relaxed mb-3">
            {phase.description}
          </p>
          <div className="text-sm text-gray-500 italic">{phase.details}</div>
        </div>
      </div>
    </div>
  );
};

export default function HackathonTimeline() {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [scrollProgress, setScrollProgress] = useState<number>(0);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const timelineElement = timelineRef.current;
      const rect = timelineElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how much of the timeline is visible
      const timelineTop = rect.top;
      const timelineHeight = rect.height;

      // Start filling when timeline enters viewport, complete when it exits
      const scrollStart = windowHeight - 200;
      const scrollEnd = -timelineHeight + 400;

      let progress = 0;
      if (timelineTop < scrollStart && timelineTop > scrollEnd) {
        progress = (scrollStart - timelineTop) / (scrollStart - scrollEnd);
      } else if (timelineTop <= scrollEnd) {
        progress = 1;
      }

      setScrollProgress(Math.min(Math.max(progress, 0), 1));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const phases: Phase[] = [
    {
      date: "Dec 20",
      title: "Registration & Team Formation",
      description:
        "Register individually or as a team (2-4 members). Review problem statements and form your dream team. This marks the beginning of your hackathon journey.",
      details:
        "Complete registration, select your track, and finalize team composition.",
    },
    {
      date: "Jan 2",
      title: "Abstract Filteration",
      description:
        "Problem statements across multiple tracks are released. Teams analyze requirements, choose their challenge, and begin ideation and planning phase.",
      details:
        "Set up repositories, plan architecture, and define your project roadmap.",
    },
    {
      date: "Jan 2- Jan 12",
      title: "Development Phase",
      description:
        "The main building phase begins! Teams work on developing their solutions, implementing features, and bringing their ideas to life.",
      details:
        "Daily standups recommended. Mentors available for guidance and technical support.",
    },
    {
      date: "Jan 12 ",
      title: "Online Presentation",
      description:
        "Submit your working prototype with a demo video, GitHub repository, and presentation deck. Minimum 50% functionality required for consideration.",
      details:
        "Ensure all deliverables are complete: code, documentation, and video demo (max 5 mins).",
    },
    {
      date: "Jan 17",
      title: "Shortlist Announcement",
      description:
        "Top 15-20 teams selected for the Grand Finale. Selections based on innovation, technical implementation, and presentation quality.",
      details:
        "Shortlisted teams will receive finalist kits and preparation materials.",
    },
    {
      date: "Jan 24",
      title: "Grand Finale",
      description:
        "Final presentations to judges panel. Teams pitch their solutions, demonstrate live functionality, and compete for prizes. Winners announced in closing ceremony.",
      details:
        "15-minute presentations followed by Q&A. Awards ceremony and networking session.",
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-10"
          }`}
        >
          <h1 className="text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white via-red-200 to-red-500 bg-clip-text text-transparent">
              Hackathon Timeline
            </span>
          </h1>
          <p className="text-xl text-gray-400 italic">
            Roadmap to the Grand Finale
          </p>
          <div className="mt-8 w-32 h-1 bg-gradient-to-r from-red-500 to-red-900 mx-auto rounded-full" />
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative">
          {phases.map((phase, index) => (
            <HackTimeline
              key={index}
              phase={phase}
              index={index}
              isVisible={isVisible}
              progress={scrollProgress}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-1000 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-red-900/20 to-black border border-red-500/30 rounded-lg p-6">
            <p className="text-gray-400">
              Ready to build something amazing? 🚀
            </p>
            <button
  disabled
  className="mt-4 px-8 py-3 
             bg-gradient-to-r from-red-900 to-red-900 
             text-white font-bold rounded-full 
             opacity-50 cursor-not-allowed"
>
  Register Now
</button>

          </div>
        </div>
        {/* Spacer for scroll */}
        <div className="h-96" />
      </div>
    </div>
  );
}
